import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useFlag } from '../../lib/flags';
import * as store from '../../lib/localStore';
import useContactForm from '../Forms/useContactForm';
import { validateEmail, validateRequired } from '../Forms/validators';
import { trackCtaClick } from '../../lib/analytics';
import './NewsletterStickyBar.css';

// Flag-gated. Shows after N seconds of dwell on blog routes only.
// Dismissed-state persists for 30 days so we don't nag a refuser.

const DWELL_BEFORE_SHOW_MS = 30_000;
const DISMISS_TTL_MS = 30 * 24 * 60 * 60 * 1000;
const ROUTE_PREFIX = '/blog';
const DISMISS_KEY = 'newsletter-sticky:dismissed';

const NewsletterStickyBar = () => {
  const enabled = useFlag('newsletterStickyBar');
  const { pathname } = useLocation();
  const onMatchingRoute = pathname.startsWith(ROUTE_PREFIX);
  const [open, setOpen] = useState(false);

  const {
    values,
    errors,
    status,
    isSubmitting,
    handleChange,
    handleSubmit,
  } = useContactForm({
    initialValues: { email: '' },
    validators: { email: [validateRequired('Email is required.'), validateEmail()] },
    serviceTag: 'newsletter',
    successMessage: "You're in. We send one quick read per fortnight.",
    formName: 'newsletter-sticky',
  });

  useEffect(() => {
    if (!enabled || !onMatchingRoute) {
      setOpen(false);
      return undefined;
    }
    if (store.get(DISMISS_KEY)) return undefined;
    const id = setTimeout(() => setOpen(true), DWELL_BEFORE_SHOW_MS);
    return () => clearTimeout(id);
  }, [enabled, onMatchingRoute]);

  // Close + persist dismissal on success so we don't show it next visit.
  useEffect(() => {
    if (status.type === 'success') {
      store.set(DISMISS_KEY, '1', { ttlMs: DISMISS_TTL_MS });
      const id = setTimeout(() => setOpen(false), 2500);
      return () => clearTimeout(id);
    }
    return undefined;
  }, [status.type]);

  const dismiss = () => {
    store.set(DISMISS_KEY, '1', { ttlMs: DISMISS_TTL_MS });
    setOpen(false);
    trackCtaClick({ label: 'newsletter_sticky_dismiss', location: 'sticky-bar' });
  };

  if (!enabled || !open) return null;

  return (
    <aside className="tn-newsletter-sticky" role="dialog" aria-label="Newsletter signup">
      <form className="tn-newsletter-sticky__form" onSubmit={handleSubmit}>
        <div className="tn-newsletter-sticky__copy">
          <strong>The fortnightly Technum read.</strong>
          <span> One short, opinionated piece on industrial tech.</span>
        </div>
        <div className="tn-newsletter-sticky__row">
          <input
            type="email"
            name="email"
            value={values.email}
            onChange={handleChange}
            disabled={isSubmitting || status.type === 'success'}
            placeholder="you@work-email.com"
            aria-invalid={!!errors.email}
            aria-label="Email address"
            className="tn-newsletter-sticky__input"
            autoComplete="email"
          />
          <button
            type="submit"
            className="tn-newsletter-sticky__submit"
            disabled={isSubmitting || status.type === 'success'}
          >
            {status.type === 'success' ? 'Subscribed' : isSubmitting ? 'Subscribing…' : 'Subscribe'}
          </button>
          <button
            type="button"
            className="tn-newsletter-sticky__close"
            onClick={dismiss}
            aria-label="Dismiss"
          >
            ×
          </button>
        </div>
        {errors.email && !status.message ? (
          <span className="tn-newsletter-sticky__msg tn-newsletter-sticky__msg--error">{errors.email}</span>
        ) : null}
        {status.message ? (
          <span className={`tn-newsletter-sticky__msg tn-newsletter-sticky__msg--${status.type || 'info'}`}>
            {status.message}
          </span>
        ) : null}
      </form>
    </aside>
  );
};

export default NewsletterStickyBar;
